import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  InstallMobile as InstallMobileIcon,
} from '@mui/icons-material';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check if running on iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    console.log('InstallPrompt: Checking PWA status...');
    console.log('InstallPrompt: isIOS =', iOS);
    console.log('InstallPrompt: isStandalone =', standalone);

    // Check if already installed, if so, don't show prompt
    if (standalone) {
      console.log('InstallPrompt: Already installed, not showing prompt');
      setShowInstallPrompt(false);
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      console.log('InstallPrompt: beforeinstallprompt event fired!');
      // Prevent the mini-infobar from appearing
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Debug: Check if event will fire (check after delay)
    let checkTimeout;
    if (!iOS) {
      checkTimeout = setTimeout(() => {
        // Check if deferredPrompt was set (will be checked via state)
        const dismissed = sessionStorage.getItem('installPromptDismissed');
        if (!dismissed) {
          console.log('InstallPrompt: beforeinstallprompt event did not fire after 5 seconds');
          console.log('InstallPrompt: This might be because:');
          console.log('  - Missing icon-512x512.png (required for installability)');
          console.log('  - User has already dismissed the prompt multiple times');
          console.log('  - PWA criteria not fully met');
          console.log('InstallPrompt: Showing manual install option as fallback');
          // Show manual install option as fallback
          setShowInstallPrompt(true);
        }
      }, 5000);
    }
    
    // For iOS, show instructions after a delay (if not dismissed)
    if (iOS) {
      const dismissed = sessionStorage.getItem('installPromptDismissed');
      if (!dismissed) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000);
      }
    }
    // For Android/Chrome, wait for beforeinstallprompt event
    // The prompt will be shown when handleBeforeInstallPrompt fires

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    sessionStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }
  
  // On desktop, show if deferredPrompt is available (browser supports install)
  // On mobile, show iOS instructions or wait for beforeinstallprompt
  // Allow showing on desktop if browser supports PWA installation
  if (!isMobile && !isIOS && !deferredPrompt) {
    // Desktop browsers that support PWA will show their own install prompt
    // We don't need to show our custom prompt if beforeinstallprompt isn't available
    return null;
  }

  // iOS installation instructions
  if (isIOS && !isStandalone && showInstallPrompt) {
    return (
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleDismiss}
        autoHideDuration={15000}
      >
        <Alert
          severity="info"
          onClose={handleDismiss}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            width: '100%',
            maxWidth: isMobile ? '90vw' : '400px',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Install App for Quick Access
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              Tap <GetAppIcon sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> 
              {' '}in Safari menu, then select "Add to Home Screen"
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  // Android/Chrome installation prompt (with deferredPrompt)
  if (deferredPrompt && showInstallPrompt) {
    return (
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleDismiss}
        autoHideDuration={8000}
      >
        <Alert
          severity="info"
          onClose={handleDismiss}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleInstallClick}
                startIcon={<InstallMobileIcon />}
                sx={{ textTransform: 'none' }}
              >
                Install
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{
            width: '100%',
            maxWidth: isMobile ? '90vw' : '400px',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Install Loan Management System
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
            Add to home screen for quick access
          </Typography>
        </Alert>
      </Snackbar>
    );
  }

  // Fallback: Manual install instructions for Chrome/Android (when beforeinstallprompt doesn't fire)
  if (!isIOS && !isStandalone && !deferredPrompt && showInstallPrompt) {
    return (
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleDismiss}
        autoHideDuration={12000}
      >
        <Alert
          severity="info"
          onClose={handleDismiss}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            width: '100%',
            maxWidth: isMobile ? '90vw' : '450px',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Install Loan Management System
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            {isMobile ? (
              <>
                Tap the <strong>menu</strong> (⋮) → <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
              </>
            ) : (
              <>
                Click the <strong>install icon</strong> (⊕) in the address bar, or go to <strong>Menu</strong> (⋮) → <strong>"Install Loan Management System"</strong>
              </>
            )}
          </Typography>
        </Alert>
      </Snackbar>
    );
  }

  return null;
};

export default InstallPrompt;

